import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  MCP_URL,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
  UrlBlock,
} from "./_components";

export function Quickstart() {
  return (
    <DocPage
      eyebrow="Quickstart"
      title="Build your first project in 5 minutes"
      intro={
        <>
          A full walkthrough: connect MCPBuild to Claude or ChatGPT, paste one
          prompt, and end up with a working project — widget, callable tool,
          public share link — without writing code. We'll build a tiny "Orders"
          dashboard that has a Cancel button per row.
        </>
      }
    >
      <DocSection id="connect" title="1. Connect the workshop to your chat client">
        <P>
          MCPBuild gives you one URL to <em>build</em> with. Paste it into Claude
          Desktop or ChatGPT's connector settings, sign in with the same email
          you use here, and the build tools (<K>dynamic_widget</K>, <K>project</K>,
          <K>remix_widget</K>) become callable from chat.
        </P>
        <UrlBlock label="Workshop MCP URL" url={MCP_URL} />
        <Callout kind="tip" title="Claude Desktop">
          Settings → Connectors → Add remote MCP server → paste the URL above →
          Approve the sign-in popup. That's it.
        </Callout>
        <Callout kind="tip" title="ChatGPT">
          Settings → Beta features → Enable connectors. New connector → MCP
          server → paste the URL above. Approve the OAuth prompt with your
          MCPBuild email.
        </Callout>
      </DocSection>

      <DocSection id="prompt" title="2. Paste this prompt into your chat">
        <P>
          One prompt builds the project, a data tool, a UI widget that calls it,
          and a trigger phrase for natural demos. Copy it, paste it into Claude
          or ChatGPT, watch it work.
        </P>
        <PromptCard
          title="One-shot Orders project"
          prompt={`Build a project called "Orders".

First, use project add_data_tool to create a tool called "cancel-order" with description "Cancel an order by its id. Returns {ok, orderId, cancelledAt}." inputSchema { type:"object", properties:{ orderId:{type:"string"} }, required:["orderId"] }, annotations { readOnlyHint: false }, data { ok: true, cancelledAt: "2026-05-27T19:00:00Z" }, and triggerPhrases ["cancel order A-100","stop the Alice order"].

Then use dynamic_widget with projectName: "Orders" and toolSlug: "orders-table" to render an orders table. Pass data: { rows: [{id:"A-100",customer:"Alice",status:"open"},{id:"A-101",customer:"Bob",status:"open"}] }. Each row gets a "Cancel" button that calls await callTool('cancel-order', { orderId: row.id }) and shows the result inline.

When done, give me the project's MCP URL and the public share link.`}
          note={
            <>
              The key bits: <K>projectName: "Orders"</K> auto-saves the widget
              into the project (no manual save step), and <K>triggerPhrases</K>{" "}
              on the data tool will surface as one-click demo pills.
            </>
          }
        />
      </DocSection>

      <DocSection id="watch" title="3. Watch the AI work">
        <P>What you should see:</P>
        <OL>
          <li>
            The AI calls <K>project add_data_tool</K> — the <K>cancel-order</K>{" "}
            tool is created inside a new <K>orders</K> project. You'll get a
            response with the project slug.
          </li>
          <li>
            The AI writes a small React widget and calls <K>dynamic_widget</K>{" "}
            with your data and <K>projectName: "Orders"</K>. The widget renders
            inline in your chat — and the same widget is saved as the project's{" "}
            <K>orders-table</K> tool, callable on its own MCP endpoint.
          </li>
          <li>
            You should see a 2-row table with Alice and Bob. Click "Cancel" next
            to either row — the widget calls back into the project's{" "}
            <K>cancel-order</K> data tool and renders the returned JSON inline.
            That's the full data-tool ↔ widget loop, end to end.
          </li>
        </OL>
      </DocSection>

      <DocSection id="verify" title="4. Open the project on the web">
        <P>
          Click <K>Projects → Orders</K> in the top nav (or visit the URL the AI
          returned). You'll see the two tools in the sidebar:
        </P>
        <UL>
          <li>
            <K>cancel-order</K> (data tool) — Preview pane shows the input
            schema + the JSON it returns. Open the <strong>Triggers</strong> tab
            to see the demo prompts you set in the prompt (and add more).
          </li>
          <li>
            <K>orders-table</K> (UI tool) — Preview pane renders the actual
            widget. Click a Cancel button here too — it works on the web exactly
            like it did in chat.
          </li>
        </UL>
        <Callout kind="note" title="If the preview is blank">
          The bundler now prints widget errors inline in the iframe. Copy the
          stack back into your chat and ask the AI to fix it via{" "}
          <K>remix_widget</K> — usually one prompt.
        </Callout>
      </DocSection>

      <DocSection id="share" title="5. Share it with someone">
        <P>You've got two paths to share, no install required for your audience:</P>
        <UL>
          <li>
            <strong>Public share link</strong> — on the project page, hit{" "}
            <K>Publish</K>. You get a <K>mcpbuild.app/p/&lt;token&gt;</K> URL
            anyone can open in a browser to see the widget rendered.
          </li>
          <li>
            <strong>Project MCP URL</strong> —{" "}
            <K>{MCP_URL}/orders</K>. If your audience uses Claude or ChatGPT,
            paste this into <em>their</em> connector. They can chat naturally
            (try "cancel order A-100") and your tool gets picked thanks to the
            trigger phrases.
          </li>
        </UL>
      </DocSection>

      <DocSection id="next" title="What to read next">
        <P>You've done the whole loop. Pick the next guide based on what you want:</P>
        <Code>{`Want to demo without coaching the audience?   → Trigger phrases & natural demos
Want to wire a tool to your real CRM/API?       → Live data, secrets & fetchData
Want to invite teammates or share a project?    → Sharing & teams
Want to re-theme a project or fork a widget?    → Remixing & forking
Curious why /mcp and /mcp/<project> are split?  → Workshop vs project endpoints`}</Code>
      </DocSection>

      <PageFooter
        next={{ to: "/docs/endpoints", label: "Workshop vs project endpoints" }}
      />
    </DocPage>
  );
}
